import type { ReactNode } from 'react';
import { errorMessage, normalizeError } from '../lib/errors';
import { formatCoverage } from '../lib/format';
import { Button, Notice } from './primitives';
import { Icon, type IconName } from './Icon';

// ─── Vacío ──────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  title?: string;
  message?: string;
  icon?: IconName;
  action?: ReactNode;
}

export function EmptyState({
  title = 'Sin resultados',
  message = 'No hay información que coincida con los filtros aplicados. Pruebe con un período más amplio o limpie los filtros.',
  icon = 'inbox',
  action,
}: EmptyStateProps): JSX.Element {
  return (
    <div className="state" role="status">
      <span className="state__icon">
        <Icon name={icon} size={19} />
      </span>
      <span className="state__title">{title}</span>
      <span className="state__msg">{message}</span>
      {action}
    </div>
  );
}

// ─── Error con reintento ────────────────────────────────────────────────────

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}

export function ErrorState({
  error,
  onRetry,
  title = 'No se pudo cargar la información',
}: ErrorStateProps): JSX.Element {
  const normalized = normalizeError(error);
  return (
    <div className="state" role="alert">
      <span className="state__icon state__icon--error">
        <Icon name="alert" size={19} />
      </span>
      <span className="state__title">{title}</span>
      <span className="state__msg">{normalized.message}</span>
      {normalized.correlationId !== undefined && (
        <span className="mono muted">Referencia: {normalized.correlationId}</span>
      )}
      {onRetry !== undefined && (
        <Button icon="refresh" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </div>
  );
}

/** Variante compacta, para avisos dentro de una tarjeta ya poblada. */
export function InlineError({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <Notice tone="error" role="alert">
      {errorMessage(error)}
      {onRetry !== undefined && (
        <>
          {' '}
          <button type="button" className="btn btn--sm btn--ghost" onClick={onRetry}>
            Reintentar
          </button>
        </>
      )}
    </Notice>
  );
}

// ─── Parcial ────────────────────────────────────────────────────────────────

export interface PartialNoticeProps {
  coverage?: number | null;
  note?: string;
  warnings?: string[];
}

/**
 * Aviso de cobertura incompleta. Se muestra SIEMPRE que algo sea `PARTIAL`.
 *
 * ── Cuándo se explica la causa y cuándo se supone ──────────────────────────
 * La frase «la sincronización del período está incompleta» es una SUPOSICIÓN sobre
 * el motivo, correcta para los indicadores que dependen de la sincronización y falsa
 * para otros. En el Reporte de Diversidad la sincronización está completa: lo que
 * falta es el atributo en 2 de 204 personas, y afirmar lo contrario manda a revisar
 * el lugar equivocado.
 *
 * Por eso, cuando quien llama entrega un `note`, ese texto REEMPLAZA la suposición
 * en lugar de acompañarla. Un motivo conocido siempre gana a uno inferido.
 */
export function PartialNotice({ coverage, note, warnings }: PartialNoticeProps): JSX.Element {
  const formatted = formatCoverage(coverage);
  return (
    <Notice tone="partial" role="status">
      <strong>Datos parciales.</strong>{' '}
      {note !== undefined
        ? note
        : formatted === null
          ? 'La sincronización del período está incompleta, por lo que las cifras pueden variar.'
          : `La sincronización del período cubre ${formatted} de lo esperado, por lo que las cifras pueden variar.`}
      {warnings !== undefined && warnings.length > 0 && (
        <ul className="tiny" style={{ marginTop: 6, display: 'grid', gap: 2 }}>
          {warnings.map((warning) => (
            <li key={warning}>· {warning}</li>
          ))}
        </ul>
      )}
    </Notice>
  );
}

/** Aviso de dato no disponible con su motivo. */
export function UnavailableNotice({ note }: { note?: string }): JSX.Element {
  return (
    <Notice tone="neutral" role="status">
      <strong>No disponible.</strong>{' '}
      {note ?? 'La fuente no está integrada o el permiso vigente no la autoriza.'}
    </Notice>
  );
}
